// @flow

import type { LocaleType } from './locale';
import { DEFAULT_LOCALE, LANGUAGE_CODE } from './locale';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import daLocale from 'date-fns/locale/da';
import enLocale from 'date-fns/locale/en-GB';
import fiLocale from 'date-fns/locale/fi';
import svLocale from 'date-fns/locale/sv';
import nbLocale from 'date-fns/locale/nb';

const dateLocale =
  LANGUAGE_CODE === 'da'
    ? daLocale
    : LANGUAGE_CODE === 'en'
    ? enLocale
    : LANGUAGE_CODE === 'fi'
    ? fiLocale
    : LANGUAGE_CODE === 'sv'
    ? svLocale
    : LANGUAGE_CODE === 'nb'
    ? nbLocale
    : null;

if (dateLocale === null) {
  throw new Error('Unknown language code');
}

type DateInputType = string | Date;

function checkDate(date) {
  return typeof date === 'string' ? date.trim() : date;
}

export function getShortDate(
  date: DateInputType,
  locale: LocaleType = DEFAULT_LOCALE
) {
  date = checkDate(date);

  switch (locale) {
    case 'da_DK':
    case 'nb_NO':
    case 'fi_FI':
    case 'en_DK':
    case 'en_FI':
    case 'en_NO':
    case 'en_SE':
    case 'en_GB':
      return format(parseISO(date), 'dd.MM.yyyy', { locale: dateLocale });
    case 'sv_SE':
    case 'sv_FI':
      return format(parseISO(date), 'yyyy.MM.dd', { locale: dateLocale });
    default:
      return '';
  }
}

export function getMonthDay(
  date: DateInputType,
  locale: LocaleType = DEFAULT_LOCALE
) {
  date = checkDate(date);

  switch (locale) {
    case 'en_DK':
    case 'en_FI':
    case 'en_NO':
    case 'en_SE':
    case 'en_GB':
    case 'sv_SE':
    case 'sv_FI':
      return format(parseISO(date), 'd MMM', { locale: dateLocale });
    case 'da_DK':
    case 'fi_FI':
    case 'nb_NO':
      return format(parseISO(date), 'd. MMM.', { locale: dateLocale });
    default:
      return '';
  }
}

export function getShortYearDate(
  date: DateInputType,
  locale: LocaleType = DEFAULT_LOCALE
) {
  date = checkDate(date);

  switch (locale) {
    case 'en_DK':
    case 'en_FI':
    case 'en_NO':
    case 'en_SE':
    case 'en_GB':
    case 'da_DK':
    case 'fi_FI':
    case 'nb_NO':
      return format(parseISO(date), 'dd.MM.yy', { locale: dateLocale });
    case 'sv_SE':
    case 'sv_FI':
      return format(parseISO(date), 'dd-MM-yy', { locale: dateLocale });
    default:
      return '';
  }
}

export function getHoursMinutes(
  date: DateInputType,
  locale: LocaleType = DEFAULT_LOCALE
) {
  date = checkDate(date);

  switch (locale) {
    case 'en_DK':
    case 'en_FI':
    case 'en_NO':
    case 'en_SE':
    case 'en_GB':
    case 'da_DK':
    case 'fi_FI':
    case 'sv_SE':
    case 'sv_FI':
    case 'nb_NO':
      return format(parseISO(date), 'HH.mm', { locale: dateLocale });
    default:
      return '';
  }
}

export function getAbbreviatedDate(
  date: DateInputType,
  locale: LocaleType = DEFAULT_LOCALE
) {
  date = checkDate(date);

  let dateFormat;
  switch (locale) {
    case 'da_DK':
    case 'nb_NO':
    case 'fi_FI':
    case 'en_NO':
      dateFormat = 'd. MMM. yyyy';
      break;
    case 'en_DK':
    case 'en_FI':
    case 'en_GB':
    case 'en_SE':
    case 'sv_SE':
    case 'sv_FI':
      dateFormat = 'd MMM yyyy';
      break;
    default:
      return date;
  }
  try {
    return format(parseISO(date), dateFormat, { locale: dateLocale }).replace(
      /\.+/g,
      '.'
    );
  } catch (e) {
    return date;
  }
}

export function getLongDate(
  date: DateInputType,
  locale: LocaleType = DEFAULT_LOCALE
) {
  date = checkDate(date);

  switch (locale) {
    case 'da_DK':
    case 'nb_NO':
    case 'fi_FI':
      return format(parseISO(date), 'd. MMMM yyyy', { locale: dateLocale });
    case 'en_DK':
    case 'en_FI':
    case 'en_GB':
    case 'en_NO':
    case 'en_SE':
    case 'sv_SE':
    case 'sv_FI':
      return format(parseISO(date), 'd MMMM yyyy', { locale: dateLocale });
    default:
      return date;
  }
}
